import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Bold, Italic, Heading2, List, ListOrdered, Link2, Wand2 } from "lucide-react";
import { MarkdownView } from "./MarkdownView.js";

/**
 * A small Markdown editor: a plain textarea that stores Markdown (so the value stays readable text
 * for the AI that reads it — question generation and scoring see "### Responsibilities", not HTML),
 * a toolbar that inserts the syntax for you, and a Preview tab rendered by the same MarkdownView the
 * read-only page uses — what you preview is what everyone sees.
 *
 * Supported (= what MarkdownView renders): # / ## / ### headings, - and 1. lists, **bold**,
 * *italic*, `code`, [links](https://…).
 */
export function MarkdownEditor({
  value,
  onChange,
  id,
  rows = 10,
  placeholder,
  showTidy = true,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  rows?: number;
  placeholder?: string;
  /** The "Tidy formatting" button (pasted text → headings + bullets). Hidden on New requirement. */
  showTidy?: boolean;
}): ReactNode {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [tab, setTab] = useState<"write" | "preview">("write");

  /** Replace the current selection with `before + selected + after`, keeping the text selected. */
  const wrap = (before: string, after: string, fallback: string): void => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const picked = value.slice(s, e) || fallback;
    const next = value.slice(0, s) + before + picked + after + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + picked.length);
    });
  };

  /** Prefix every line touched by the selection (lists, headings). Toggles off when all lines already have it. */
  const prefixLines = (make: (i: number) => string, strip: RegExp): void => {
    const el = ref.current;
    if (!el) return;
    const start = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const endNl = value.indexOf("\n", el.selectionEnd);
    const end = endNl === -1 ? value.length : endNl;
    const lines = value.slice(start, end).split("\n");
    const allHave = lines.every((l) => strip.test(l) || l.trim() === "");
    const out = lines
      .map((l, i) => (l.trim() === "" ? l : allHave ? l.replace(strip, "") : make(i) + l.replace(strip, "")))
      .join("\n");
    onChange(value.slice(0, start) + out + value.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + out.length);
    });
  };

  /**
   * Tidy a pasted job description: a short line with no closing punctuation that is followed by
   * text becomes a ### heading; the lines under a heading that read as items become "- " bullets.
   * Purely a starting point — the recruiter sees the result in the box and can undo with Ctrl/⌘+Z.
   */
  const tidy = (): void => {
    const lines = value.replace(/\r\n/g, "\n").split("\n");
    const isHeading = (l: string, next: string | undefined): boolean =>
      /^[A-Z][^.!?:;,]{1,48}:?$/.test(l.trim()) && !/^(#|-|\*|\d+\.)/.test(l.trim()) && !!next && next.trim() !== "";
    let underHeading = false;
    const out = lines.map((raw, i) => {
      const l = raw.trim();
      if (!l) { underHeading = false; return ""; }
      if (/^(#{1,3} |[-*•] |\d+\. )/.test(l)) return l.replace(/^•\s*/, "- ");
      if (i > 0 && isHeading(l, lines[i + 1])) { underHeading = true; return `### ${l.replace(/:$/, "")}`; }
      // a one-sentence line under a heading is an item; anything with a sentence break inside it
      // ("…Inventory. The ideal candidate…") or longer than a list item is a paragraph and stays one
      if (underHeading && l.length < 180 && !/[.!?]\s+\S/.test(l)) return `- ${l}`;
      return l;
    });
    // first line that is just the role title → H2
    if (out[0] && out[0].length < 70 && !/^(#|-)/.test(out[0]) && !/[.!?]$/.test(out[0])) out[0] = `## ${out[0]}`;
    onChange(out.join("\n").replace(/\n{3,}/g, "\n\n"));
  };

  const tools: { label: string; icon: ReactNode; run: () => void }[] = [
    { label: "Bold", icon: <Bold size={15} />, run: () => wrap("**", "**", "bold text") },
    { label: "Italic", icon: <Italic size={15} />, run: () => wrap("*", "*", "italic text") },
    { label: "Heading", icon: <Heading2 size={15} />, run: () => prefixLines(() => "### ", /^#{1,3}\s+/) },
    { label: "Bulleted list", icon: <List size={15} />, run: () => prefixLines(() => "- ", /^[-*•]\s+/) },
    { label: "Numbered list", icon: <ListOrdered size={15} />, run: () => prefixLines((i) => `${i + 1}. `, /^\d+\.\s+/) },
    { label: "Link", icon: <Link2 size={15} />, run: () => wrap("[", "](https://)", "link text") },
  ];

  return (
    <div className="t1-md">
      <div className="t1-md-bar">
        <div className="t1-md-tabs" role="tablist" aria-label="Editor mode">
          <button type="button" role="tab" aria-selected={tab === "write"} className={tab === "write" ? "on" : ""} onClick={() => setTab("write")}>Write</button>
          <button type="button" role="tab" aria-selected={tab === "preview"} className={tab === "preview" ? "on" : ""} onClick={() => setTab("preview")}>Preview</button>
        </div>
        {tab === "write" && (
          <div className="t1-md-tools" role="toolbar" aria-label="Formatting">
            {tools.map((t) => (
              <button key={t.label} type="button" onClick={t.run} aria-label={t.label} data-tip={t.label}>
                {t.icon}
              </button>
            ))}
            {showTidy && (
              <>
                <span className="t1-md-sep" aria-hidden="true" />
                <button type="button" className="t1-md-tidy" onClick={tidy} disabled={!value.trim()} data-tip="Turn pasted text into headings and bullets" data-tip-pos="left">
                  <Wand2 size={15} /> Tidy formatting
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {tab === "write" ? (
        <textarea
          id={id}
          ref={ref}
          className="t1-md-input"
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**", "**", "bold text"); }
            if (mod && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*", "*", "italic text"); }
          }}
        />
      ) : (
        <div className="t1-md-preview">
          {value.trim() ? <MarkdownView content={value} /> : <p className="mut sm">Nothing to preview yet.</p>}
        </div>
      )}
      <p className="t1-md-hint">Markdown: ### heading · - bullet · 1. numbered · **bold** · *italic*</p>
    </div>
  );
}
